import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { EyeSlash, Eye } from "../../../../assets/svg/";
import { Layout } from "../../../../components/Layout";
import {
  Container,
  ContentAboutUser,
  DataUser,
  HeaderAboutUser,
  Item,
  LeftSide,
  OtherItems,
  PictureUser,
  RightSide,
  TitleCard,
  Form,
} from "./styles";
import NoImage from "../../../../assets/jpg/no_image.jpg";
import { useNotification } from "../../../../hooks/useNotification";
import { Input } from "../../../../components/Input";
import { Button } from "../../../../components/Button";
import { useAuth } from "../../../../hooks/useAuth";
import { IFormNewPassword, IPropsUserProfile } from "../../interfaces";
import { useNavigate } from "react-router-dom";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import { useTranslate } from "../../../../hooks/useTranslate";
import { format } from "date-fns";
import { parseMultiple } from "../../../../helpers/date";

const UserProfile = () => {
  const { errorNotification, successNotification } = useNotification();
  const { user, changePassword } = useAuth();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const [showCurrentPassword, setCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const schema = yup.object({
    currentPassword: yup
      .string()
      .required(translate("profile.error_current_password")),
    newPassword: yup.string().required(translate("profile.error_new_password")),
    confirmNewPassword: yup
      .string()
      .required(translate("profile.error_confirm_password_required"))
      .oneOf(
        [yup.ref("newPassword"), null],
        translate("profile.error_confirm_password")
      ),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormNewPassword>({
    resolver: yupResolver(schema),
  });

  const submitForm = async ({
    currentPassword,
    newPassword,
  }: IFormNewPassword) => {
    try {
      await changePassword({
        newPassword,
        password: currentPassword,
      });
      reset();
      successNotification(translate("profile.success_message_password"));
    } catch (error) {
      errorNotification(translate("profile.error_message_password"));
    }
  };

  const ItemUserProfile = ({ label, value }: IPropsUserProfile) => {
    return (
      <Item>
        <span>{label}</span>
        <p>{value ?? translate("global.uninformed")}</p>
      </Item>
    );
  };

  const navigateUser = () => {
    navigate(`/profile/edit/${user.id}`);
  };

  return (
    <Layout>
      <HeaderTitle title={translate("profile.title_view")} />
      <Container>
        <LeftSide>
          <TitleCard>{translate("profile.personal_data")}</TitleCard>
          <ContentAboutUser>
            <HeaderAboutUser>
              <PictureUser src={NoImage} />
              <DataUser>
                <ItemUserProfile
                  label={translate("profile.item_name")}
                  value={user.name}
                />
                <ItemUserProfile
                  label={translate("profile.item_email")}
                  value={user.email}
                />
              </DataUser>
            </HeaderAboutUser>
            <OtherItems>
              <ItemUserProfile
                label={translate("profile.item_phone")}
                value={user.phone}
              />
              <ItemUserProfile
                label={translate("profile.item_dob")}
                value={
                  user.dob
                    ? format(
                        parseMultiple(
                          user.dob as any,
                          ["yyyy-MM-dd", "dd/MM/yyyy", "ddMMyyyy"],
                          new Date()
                        ),
                        "dd/MM/yyyy"
                      )
                    : null
                }
              />
              <ItemUserProfile
                label={translate("profile.item_cpf")}
                value={user.cpf}
              />
              <ItemUserProfile
                label={translate("profile.item_office")}
                value={user.office}
              />
              <ItemUserProfile
                label={translate("profile.item_permissions")}
                value=""
              />
              <Button
                type="button"
                label={translate("profile.title_edit")}
                onClick={navigateUser}
              />
            </OtherItems>
          </ContentAboutUser>
        </LeftSide>
        <RightSide>
          <TitleCard>{translate("profile.preferences")}</TitleCard>
          <Form onSubmit={handleSubmit(submitForm)}>
            <Input
              {...register("currentPassword")}
              error={errors.currentPassword?.message}
              placeholder={translate("profile.current_password")}
              type={showCurrentPassword ? "text" : "password"}
              cbIcon={() => {
                setCurrentPassword(!showCurrentPassword);
              }}
              icon={showCurrentPassword ? <EyeSlash /> : <Eye />}
            />
            <Input
              {...register("newPassword")}
              error={errors.newPassword?.message}
              placeholder={translate("profile.new_password")}
              type={showNewPassword ? "text" : "password"}
              cbIcon={() => {
                setShowNewPassword(!showNewPassword);
              }}
              icon={showNewPassword ? <EyeSlash /> : <Eye />}
            />
            <Input
              {...register("confirmNewPassword")}
              error={errors.confirmNewPassword?.message}
              placeholder={translate("profile.confirm_password")}
              type={showConfirmNewPassword ? "text" : "password"}
              cbIcon={() => {
                setShowConfirmNewPassword(!showConfirmNewPassword);
              }}
              icon={showConfirmNewPassword ? <EyeSlash /> : <Eye />}
            />

            <Button label={translate("profile.btn_save")} type="submit" />
          </Form>
        </RightSide>
      </Container>
    </Layout>
  );
};

export { UserProfile };
